import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { IconButton, IconButtonThemes } from '@wix/wix-ui-tpa';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common/on-stage';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { useRootProps } from 'AiAssistantWidget/Widget/contexts';

import { st, classes, cssStates } from './ScrollingGallery.st.css';

type ScrollingGalleryProps = {
  arrowPosition?: number | 'center';
  children: React.ReactNode;
  arialLabel?: string;
  totalItems?: number;
};

export const ScrollingGallery = ({
  arrowPosition = 'center',
  children,
  arialLabel,
  totalItems,
}: ScrollingGalleryProps) => {
  const { t } = useTranslation();
  const { direction } = useRootProps();
  const isRTL = direction === 'rtl';

  const arrowOffset = useMemo(
    () => (arrowPosition === 'center' ? '50%' : `${arrowPosition}px`),
    [arrowPosition],
  );

  const [arrowState, setArrowState] = useState({
    previous: false,
    next: false,
  });
  const [currentCardIndex, setCurrentCardIndex] = useState(1);
  const [announcement, setAnnouncement] = useState('');

  const scroller = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const totalCards = totalItems ?? React.Children.count(children);

  const getNearestItemIndex = useCallback(() => {
    if (!scroller.current || !itemsRef.current) {
      return null;
    }

    const items = Array.from(itemsRef.current.children) as HTMLElement[];
    if (!items.length) {
      return null;
    }

    const scrollerRect = scroller.current.getBoundingClientRect();
    const viewportCenter = scrollerRect.left + scrollerRect.width / 2;

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(itemCenter - viewportCenter);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }, []);

  const scrollToCard = useCallback((targetIndex: number) => {
    if (!scroller.current || !itemsRef.current) {
      return;
    }

    const items = Array.from(itemsRef.current.children) as HTMLElement[];
    const targetItem = items[targetIndex];
    if (!targetItem) {
      return;
    }

    targetItem.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth',
    });
  }, []);

  const calculateCurrentCard = useCallback(() => {
    const nearestIndex = getNearestItemIndex();

    if (nearestIndex === null || totalCards <= 0) {
      return;
    }

    const newIndex = Math.min(nearestIndex + 1, totalCards);
    setCurrentCardIndex((previousIndex) => {
      if (previousIndex === newIndex) {
        return previousIndex;
      }

      setAnnouncement(
        t('app.widget.ariallabel.cardPosition', {
          current: newIndex,
          total: totalCards,
        }),
      );
      return newIndex;
    });
  }, [getNearestItemIndex, t, totalCards]);

  const updateArrowState = useCallback(() => {
    if (!scroller.current) {
      return;
    }

    const nearestIndex = getNearestItemIndex();
    if (nearestIndex === null || totalCards <= 1) {
      setArrowState((currentArrowState) => {
        if (!currentArrowState.previous && !currentArrowState.next) {
          return currentArrowState;
        }

        return { previous: false, next: false };
      });
      return;
    }

    const previous = nearestIndex > 0;
    const next = nearestIndex < totalCards - 1;

    setArrowState((currentArrowState) => {
      if (
        currentArrowState.previous === previous &&
        currentArrowState.next === next
      ) {
        return currentArrowState;
      }

      return { previous, next };
    });
  }, [getNearestItemIndex, totalCards]);

  const onClickArrow = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      if (totalCards <= 1) {
        return;
      }

      const currentIndex = Math.max(0, currentCardIndex - 1);

      switch (e.currentTarget.name) {
        case 'previous': {
          scrollToCard(Math.max(0, currentIndex - 1));
          break;
        }
        case 'next': {
          scrollToCard(Math.min(totalCards - 1, currentIndex + 1));
          break;
        }
      }
    },
    [currentCardIndex, scrollToCard, totalCards],
  );

  useLayoutEffect(() => {
    const onScroll = () => {
      updateArrowState();
      calculateCurrentCard();
    };

    const currentScroller = scroller.current;
    if (!currentScroller) {
      return;
    }

    updateArrowState();
    calculateCurrentCard();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateArrowState();
        calculateCurrentCard();
      });

      resizeObserver.observe(currentScroller);
      if (itemsRef.current) {
        resizeObserver.observe(itemsRef.current);
      }
    }

    currentScroller.addEventListener('scroll', onScroll);

    return () => {
      currentScroller.removeEventListener('scroll', onScroll);
      resizeObserver?.disconnect();
    };
  }, [calculateCurrentCard, updateArrowState]);

  useEffect(() => {
    if (totalCards > 0) {
      setCurrentCardIndex(1);
      setAnnouncement(
        t('app.widget.ariallabel.cardPosition', {
          current: 1,
          total: totalCards,
        }),
      );
      return;
    }

    setCurrentCardIndex(0);
    setAnnouncement('');
  }, [totalCards, t]);

  return (
    <div className={st(classes.root)} role="region" aria-label={arialLabel}>
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {announcement}
      </div>
      <IconButton
        className={st(
          classes.arrow,
          isRTL ? classes.arrowRight : classes.arrowLeft,
          cssStates({ visible: arrowState.previous }),
        )}
        style={{ top: arrowOffset }}
        theme={IconButtonThemes.Box}
        icon={isRTL ? <ChevronRight /> : <ChevronLeft />}
        name="previous"
        aria-label={t('app.widget.ariallabel.previousItem')}
        aria-disabled={!arrowState.previous}
        tabIndex={arrowState.previous ? 0 : -1}
        onClick={onClickArrow}
      />

      <div className={st(classes.scroller)} ref={scroller}>
        <div className={st(classes.flexLayoutSizeFix)}>
          <div className={st(classes.scrollerItems)} ref={itemsRef}>
            {children}
          </div>
        </div>
      </div>

      <IconButton
        className={st(
          classes.arrow,
          isRTL ? classes.arrowLeft : classes.arrowRight,
          cssStates({ visible: arrowState.next }),
        )}
        style={{ top: arrowOffset }}
        theme={IconButtonThemes.Box}
        icon={isRTL ? <ChevronLeft /> : <ChevronRight />}
        name="next"
        aria-label={t('app.widget.ariallabel.nextItem')}
        aria-disabled={!arrowState.next}
        tabIndex={arrowState.next ? 0 : -1}
        onClick={onClickArrow}
      />
    </div>
  );
};
